import { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Volume2, VolumeX } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {AutoTranslate} from "@/lib/autoTranslate.tsx";
import { useTranslation} from "react-i18next";
interface GuidedTourProps {
  step: {
    id: number;
    title: string;
    text: string;
    target: string;
    position: 'left' | 'right';
  };
  totalSteps: number;
  onNext: () => void;
  onCancel: () => void;
}

export const GuidedTour = ({ step, totalSteps, onNext, onCancel }: GuidedTourProps) => {
  const transText = useTranslation().t('autoTranslate', { returnObjects: true });
  const { i18n } = useTranslation();

  // scroll to target element when step changes


  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [resolvedPosition, setResolvedPosition] = useState<'left' | 'right'>(step.position);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isMobile = window.innerWidth < 768;


  // scroll to target element when step changes
  const scrollToTarget = () => {
    const targetElement = document.querySelector(step.target);
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };


  useEffect(() => {
    scrollToTarget();
  }, [step]);

  // Lock page scroll while tour is active
  useEffect(() => {

    const originalOverflow = document.body.style.overflow;
    const originalTouchAction = document.body.style.touchAction;

    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.touchAction = originalTouchAction;
    };
  }, []);

  useLayoutEffect(() => {
    const targetElement = document.querySelector(step.target) as HTMLElement | null;
    if (!targetElement) return;
    // Prevent one-frame arrow mismatch when switching steps

    const rect = targetElement.getBoundingClientRect();
    console.log('Target Rect:', rect);
    const cardWidth = isMobile ? window.innerWidth - 40 : 400;
    const cardHeight = isMobile ? window.innerHeight / 3 : 250;
    const padding = isMobile ? 20 : 10;
    const gap = isMobile ? 20 : 30; // space between target and card

    let finalPosition = step.position;

    // Decide best side FIRST
    const spaceLeft = rect.left;
    const spaceRight = window.innerWidth - rect.right;

    if (step.position === 'left' && spaceLeft < cardWidth + gap) finalPosition = 'right';
    if (step.position === 'right' && spaceRight < cardWidth + gap) finalPosition = 'left';
    setResolvedPosition(finalPosition);
    console.log('Final Position:', finalPosition);

    // Bias toward upper interaction zone instead of geometric center
    const visualAnchorY = rect.top + rect.height * 0.35;
    let top = visualAnchorY - cardHeight / 2;

    let left = 0;

    if (finalPosition === 'left') {
      left = rect.left - cardWidth - gap;
    }

    if (finalPosition === 'right') {
      left = rect.right + gap;
    }

    // Clamp to viewport
    const maxLeft = window.innerWidth - cardWidth - padding;
    const maxTop = window.innerHeight - cardHeight - padding;

    left = Math.max(padding, Math.min(left, maxLeft));
    top = Math.max(padding, Math.min(top, maxTop));

    setPosition({ top, left });
    console.log('Card Position:', { top, left });

    // Recalculate position when layout changes (chat grows, window resizes, scroll, etc.)
    const updatePosition = () => {
      const newRect = targetElement.getBoundingClientRect();
      const visualY = newRect.top + newRect.height * 0.35;
      let newTop = visualY - cardHeight / 2;

      let newLeft =
        finalPosition === 'left'
          ? newRect.left - cardWidth - gap
          : newRect.right + gap;

      const maxLeft = window.innerWidth - cardWidth - padding;
      const maxTop = window.innerHeight - cardHeight - padding;

      newLeft = Math.max(padding, Math.min(newLeft, maxLeft));
      newTop = Math.max(padding, Math.min(newTop, maxTop));

      setPosition({ top: newTop, left: newLeft });
    };

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    targetElement.classList.add(isMobile ? 'tour-highlight-mobile' : 'tour-highlight');
    return () => {
      targetElement.classList.remove('tour-highlight', 'tour-highlight-mobile');
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [step, resolvedPosition]);

  useEffect(() => {
    if (audioEnabled) {
      playAudio();
    }
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [step, audioEnabled]);

  const playAudio = async () => {
    try {
      setIsSpeaking(true);

      const { data, error } = await supabase.functions.invoke('text-to-speech', {
        body: { text: step.text }
      });

      if (error) throw error;

      if (data?.audioContent) {
        const audio = new Audio(`data:audio/mpeg;base64,${data.audioContent}`);
        audioRef.current = audio;

        audio.onended = () => {
          setIsSpeaking(false);
        };

        await audio.play();
      }
    } catch (error) {
      console.error('Error playing audio:', error);
      setIsSpeaking(false);
      toast.error('Could not play audio guide');
    }
  };

  const toggleAudio = () => {
    audioEnabled ? setAudioEnabled(false) : setAudioEnabled(true);
    if (audioEnabled && !isSpeaking) {
      playAudio();
    }
    else if (!audioEnabled && isSpeaking) {
      audioRef.current?.pause();
      setIsSpeaking(false);
    }
  };

  const getArrowPosition = () => {
    if (isMobile) return { className: '', style: {} as React.CSSProperties };
    const targetElement = document.querySelector(step.target) as HTMLElement | null;
    if (!targetElement) return { className: '', style: {} as React.CSSProperties };

    const rect = targetElement.getBoundingClientRect();
    const arrowMargin = 16; // keep arrow away from card corners

    // Card position is already viewport-based (fixed)
    const cardTop = position.top;
    const cardLeft = position.left;
    const cardWidth = 400;
    const cardHeight = 250;

    const targetCenterX = rect.left + rect.width / 2;
    const targetCenterY = rect.top + rect.height * 0.35;

    switch (resolvedPosition) {
      case 'left': {
        let top = targetCenterY - cardTop;
        top = Math.max(arrowMargin, Math.min(top, cardHeight - arrowMargin));
        return {
          className:
            'tour-arrow absolute right-[-12px] border-t-8 border-b-8 border-l-8 border-t-transparent border-b-transparent border-l-white dark:border-l-white',
          style: { top, transform: 'translateY(-50%)' }
        };
      }

      case 'right': {
        let top = targetCenterY - cardTop;
        top = Math.max(arrowMargin, Math.min(top, cardHeight - arrowMargin));
        return {
          className:
            'tour-arrow absolute left-[-12px] border-t-8 border-b-8 border-r-8 border-t-transparent border-b-transparent border-r-white dark:border-r-white',
          style: { top, transform: 'translateY(-50%)' }
        };
      }
      default:
        return { className: '', style: {} };
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40 md:pointer-events-auto pointer-events-none" onClick={onCancel} />

      <Card
        aria-live="assertive"
        className={`fixed z-50 shadow-2xl animate-in duration-300 ${isMobile
          ? 'left-4 right-4 bottom-4 rounded-2xl slide-in-from-bottom'
          : 'w-[400px] fade-in'
          }`}
        style={
          isMobile
            ? undefined
            : { top: `${position.top}px`, left: `${position.left}px` }
        }
      >
        {(() => {
          const arrow = getArrowPosition();
          return <div className={`w-0 h-0 ${arrow.className}`} style={arrow.style} />;
        })()}

        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="flex items-center gap-2">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold">
                  {step.id}
                </span>
                {step.title}
              </CardTitle>
              <CardDescription className="mt-2">
                Step {step.id} of {totalSteps}
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleAudio}
              className="shrink-0"
              aria-label={audioEnabled ? "Mute audio" : "Enable audio"}
            >
              {audioEnabled ? (
                <Volume2 className="h-4 w-4 text-primary animate-pulse" />
              ) : (
                <VolumeX className="h-4 w-4" />
              )}
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          <p className="text-sm mb-4">{step.text}</p>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={onCancel}
              className="flex-1"
            >
              {AutoTranslate(`Skip Tour`)}
            </Button>
            <Button
              onClick={onNext}
              className="flex-1"
            >
              {step.id === totalSteps ? 'Finish' : 'Next'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <style>{`
        .tour-highlight {
          position: relative;
          z-index: 45;
          box-shadow: 0 0 0 4px hsl(var(--primary)), 0 0 0 9999px rgba(0, 0, 0, 0.5);
          border-radius: 8px;
        }
        .tour-highlight-mobile {
          position: relative;
          z-index: 45;
          border-radius: 12px;
          box-shadow:
            0 0 0 3px hsl(var(--primary)),
            0 0 0 6px hsl(var(--background)),
            0 0 25px 8px rgba(0, 0, 0, 0.6),
            0 0 0 9999px rgba(0, 0, 0, 0.65);
          animation: tourPulse 1.8s ease-in-out infinite;
        }

        @keyframes tourPulse {
          0% {
            box-shadow:
              0 0 0 3px hsl(var(--primary)),
              0 0 0 6px hsl(var(--background)),
              0 0 25px 8px rgba(0, 0, 0, 0.6),
              0 0 0 9999px rgba(0, 0, 0, 0.65);
          }
          50% {
            box-shadow:
              0 0 0 4px hsl(var(--primary)),
              0 0 0 8px hsl(var(--background)),
              0 0 35px 12px rgba(0, 0, 0, 0.75),
              0 0 0 9999px rgba(0, 0, 0, 0.7);
          }
          100% {
            box-shadow:
              0 0 0 3px hsl(var(--primary)),
              0 0 0 6px hsl(var(--background)),
              0 0 25px 8px rgba(0, 0, 0, 0.6),
              0 0 0 9999px rgba(0, 0, 0, 0.65);
          }
        }
      `}</style>
    </>
  );
};
